import { isProduction } from './environment-helper';

// Advanced Meta Tags helper functions - only work in production
class AdvancedMetaHelper {
  // Update meta robots tag
  static updateRobots(robots = 'index, follow') {
    if (!isProduction()) return;

    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = robots;
  }

  // Update meta viewport for mobile optimization
  static updateViewport(viewport = 'width=device-width, initial-scale=1.0, maximum-scale=5.0') {
    if (!isProduction()) return;

    let metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      metaViewport.content = viewport;
    }
  }

  // Add geo meta tags for location-based content
  static updateGeoTags(geoData) {
    if (!isProduction() || !geoData) return;

    const geoTags = {
      'geo.region': geoData.region,
      'geo.placename': geoData.placename,
      'geo.position': geoData.position, // lat;lng format
      ICBM: geoData.position, // Alternative format
    };

    Object.entries(geoTags).forEach(([name, content]) => {
      if (!content) return;

      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = name;
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    });
  }

  // Add language and locale meta tags
  static updateLanguageTags(lang = 'tr', locale = 'tr_TR') {
    if (!isProduction()) return;

    // Update html lang attribute
    document.documentElement.lang = lang;

    // Update meta language
    let metaLang = document.querySelector('meta[name="language"]');
    if (!metaLang) {
      metaLang = document.createElement('meta');
      metaLang.name = 'language';
      document.head.appendChild(metaLang);
    }
    metaLang.content = lang;

    // Update locale meta tag
    let metaLocale = document.querySelector('meta[property="og:locale"]');
    if (!metaLocale) {
      metaLocale = document.createElement('meta');
      metaLocale.property = 'og:locale';
      document.head.appendChild(metaLocale);
    }
    metaLocale.content = locale;
  }

  // Add hreflang tags for multilingual content
  static updateHreflangTags(currentLang, alternativeUrls) {
    if (!isProduction()) return;

    // Remove existing hreflang tags
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflang.forEach((tag) => tag.remove());

    // Add hreflang tags for each language
    Object.entries(alternativeUrls).forEach(([lang, url]) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = url;
      document.head.appendChild(link);
    });

    // Add x-default hreflang (usually English)
    if (alternativeUrls.en) {
      const defaultLink = document.createElement('link');
      defaultLink.rel = 'alternate';
      defaultLink.hreflang = 'x-default';
      defaultLink.href = alternativeUrls.en;
      document.head.appendChild(defaultLink);
    }
  }

  // Add preload/prefetch tags for performance
  static addResourceHints(resources) {
    if (!isProduction() || !resources) return;

    resources.forEach((resource) => {
      const link = document.createElement('link');
      link.rel = resource.rel; // preload, prefetch, dns-prefetch, preconnect
      link.href = resource.href;

      if (resource.as) link.as = resource.as; // script, style, font, image
      if (resource.type) link.type = resource.type;
      if (resource.crossorigin) link.crossOrigin = resource.crossorigin;

      document.head.appendChild(link);
    });
  }

  // Add security meta tags
  static addSecurityMeta() {
    if (!isProduction()) return;

    const securityTags = [
      { name: 'referrer', content: 'strict-origin-when-cross-origin' },
      { 'http-equiv': 'X-Content-Type-Options', content: 'nosniff' },
      { 'http-equiv': 'X-Frame-Options', content: 'DENY' },
      { 'http-equiv': 'X-XSS-Protection', content: '1; mode=block' },
    ];

    securityTags.forEach((tag) => {
      const meta = document.createElement('meta');
      if (tag.name) meta.name = tag.name;
      if (tag['http-equiv']) meta.httpEquiv = tag['http-equiv'];
      meta.content = tag.content;
      document.head.appendChild(meta);
    });
  }

  // Generate comprehensive meta tags for coach pages
  static generateCoachMetaTags(coach, lang = 'tr') {
    if (!isProduction()) return null;

    const coachName = `${coach.firstName} ${coach.lastName}`;
    const baseUrl = 'https://www.coachingsearchengine.com';

    return {
      robots: 'index, follow, max-snippet:160, max-image-preview:large',
      language: lang,
      locale: lang === 'tr' ? 'tr_TR' : 'en_US',
      geoData:
        coach.city && coach.country
          ? {
              region: coach.country,
              placename: coach.city,
              position: coach.coordinates, // if available
            }
          : null,
      hreflang: {
        tr: `${baseUrl}/tr/coach/${coach.slug}`,
        en: `${baseUrl}/en/coach/${coach.slug}`,
      },
      resourceHints: [
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://api.coachingsearchengine.com' },
        { rel: 'dns-prefetch', href: 'https://www.google-analytics.com' },
      ],
    };
  }

  // Generate comprehensive meta tags for category pages
  static generateCategoryMetaTags(category, lang = 'tr') {
    if (!isProduction()) return null;

    const baseUrl = 'https://www.coachingsearchengine.com';

    return {
      robots: 'index, follow, max-snippet:160, max-image-preview:large',
      language: lang,
      locale: lang === 'tr' ? 'tr_TR' : 'en_US',
      hreflang: {
        tr: `${baseUrl}/tr/kategori/${category.id}-id-${category.slugTr}`,
        en: `${baseUrl}/en/category/${category.id}-id-${category.slugEn}`,
      },
      resourceHints: [
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://api.coachingsearchengine.com' },
        { rel: 'dns-prefetch', href: 'https://www.google-analytics.com' },
      ],
    };
  }

  // Generate comprehensive meta tags for homepage
  static generateHomepageMetaTags(lang = 'tr') {
    if (!isProduction()) return null;

    const baseUrl = 'https://www.coachingsearchengine.com';

    return {
      robots: 'index, follow, max-snippet:160, max-image-preview:large',
      language: lang,
      locale: lang === 'tr' ? 'tr_TR' : 'en_US',
      hreflang: {
        tr: `${baseUrl}/tr`,
        en: `${baseUrl}/en`,
      },
      resourceHints: [
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://api.coachingsearchengine.com' },
        { rel: 'dns-prefetch', href: 'https://www.google-analytics.com' },
        // Removed hero image preload - causing warnings in production
      ],
    };
  }

  // Apply all advanced meta tags
  static applyAdvancedMeta(metaConfig) {
    if (!isProduction() || !metaConfig) return;

    // Update basic meta tags
    if (metaConfig.robots) this.updateRobots(metaConfig.robots);
    if (metaConfig.language) this.updateLanguageTags(metaConfig.language, metaConfig.locale);

    // Update geo tags
    if (metaConfig.geoData) this.updateGeoTags(metaConfig.geoData);

    // Update hreflang tags
    if (metaConfig.hreflang) this.updateHreflangTags(metaConfig.language, metaConfig.hreflang);

    // Add resource hints
    if (metaConfig.resourceHints) this.addResourceHints(metaConfig.resourceHints);

    // Add security meta tags (once)
    if (!document.querySelector('meta[name="referrer"]')) {
      this.addSecurityMeta();
    }
  }
}

export default AdvancedMetaHelper;
